import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { PageHeaderComponent } from '@shared/components/layout';
import { ConfigurationNavigationStateService } from '../../services/configuration-navigation-state.service';

interface ConfigurationOption {
  title: string;
  description: string;
  icon: string;
  route: string;
}

interface ConfigurationSection {
  title: string;
  description: string;
  options: ConfigurationOption[];
}

@Component({
  selector: 'app-configuration',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatTabsModule, PageHeaderComponent],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent {
  private readonly navigationState = inject(ConfigurationNavigationStateService);
  readonly search = this.navigationState.search;
  readonly sections: ConfigurationSection[] = [
    {
      title: 'Colaboración', description: 'Áreas, ministerios y funciones de las personas.',
      options: [
        { title: 'Áreas', description: 'Áreas para permisos, nombramientos y colaboraciones.', icon: 'category', route: '/configuration/areas' },
        { title: 'Ministerios', description: 'Apóstol, Pastor y otros ministerios.', icon: 'volunteer_activism', route: '/configuration/ministries' },
        { title: 'Encargos ministeriales', description: 'Encargos disponibles para los ministros.', icon: 'assignment_ind', route: '/configuration/ministry-appointments' },
        { title: 'Nombramientos', description: 'Nombramientos disponibles por área.', icon: 'badge', route: '/configuration/assignments' },
        { title: 'Colaboraciones', description: 'Colaboraciones y roles de las personas.', icon: 'groups', route: '/configuration/activities' }
      ]
    },
    {
      title: 'Comunidad', description: 'Tipos de servicios y acontecimientos de las comunidades.',
      options: [
        { title: 'Tipos de servicios divinos', description: 'Servicios disponibles en el sistema.', icon: 'church', route: '/configuration/divine-service-types' },
        { title: 'Tipos de eventos de comunidades', description: 'Acontecimientos para el historial de comunidades.', icon: 'event_note', route: '/configuration/community-event-types' }
      ]
    },
    {
      title: 'NAC Portal', description: 'Prepará, importá y revisá los datos del Portal.',
      options: [
        { title: 'Equivalencias NAC Portal', description: 'Configurá las equivalencias antes de importar.', icon: 'sync_alt', route: '/configuration/portal-import-equivalences' },
        { title: 'Importar miembros', description: 'Cargá una exportación JSON con revisión previa.', icon: 'upload_file', route: '/configuration/portal-member-import' },
        { title: 'Historial de importaciones', description: 'Resultados y pendientes de los lotes importados.', icon: 'history', route: '/configuration/portal-import-history' }
      ]
    },
    {
      title: 'Administración', description: 'Gestión de miembros y funcionalidades del sistema.',
      options: [
        { title: 'Todos los miembros', description: 'Consultá y administrá miembros activos y eliminados.', icon: 'manage_accounts', route: '/configuration/deleted-members' },
        { title: 'Tareas de equipo', description: 'Habilitá o deshabilitá la funcionalidad en este ambiente.', icon: 'task_alt', route: '/configuration/team-tasks' }
      ]
    }
  ];

  readonly filteredSections = computed(() => {
    const query = this.normalize(this.search().trim());
    return this.sections.map(section => ({
      ...section,
      options: section.options.filter(option => this.normalize(`${section.title} ${option.title} ${option.description}`).includes(query))
    })).filter(section => section.options.length > 0);
  });

  readonly selectedTabIndex = computed(() => Math.max(0,
    this.filteredSections().findIndex(section => section.title === this.navigationState.selectedSection())
  ));

  selectTab(index: number): void {
    const section = this.filteredSections()[index];
    if (section) this.navigationState.selectedSection.set(section.title);
  }

  private normalize(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }
}
